'use client';

import { useState, useEffect, useCallback } from 'react';
import {
	getDevToolsPreferences,
	saveDevToolsPreferences,
	DevToolsPreferences,
	DevToolsPosition
} from '../utils';

export const useDevToolsPreferences = () => {
	const [preferences, setPreferences] = useState<DevToolsPreferences>({
		showDevTools: true,
		position: 'bottom-left'
	});
	const [isHydrated, setIsHydrated] = useState(false);

	// Load saved preferences after hydration
	useEffect(() => {
		const savedPreferences = getDevToolsPreferences();
		setPreferences(savedPreferences);
		setIsHydrated(true);
	}, []);

	const updatePreferences = useCallback((newPreferences: Partial<DevToolsPreferences>) => {
		setPreferences((prev) => {
			const updated = { ...prev, ...newPreferences };
			saveDevToolsPreferences(updated);
			return updated;
		});
	}, []);

	const hideDevTools = useCallback(() => {
		updatePreferences({ showDevTools: false });
	}, [updatePreferences]);

	const showDevTools = useCallback(() => {
		updatePreferences({ showDevTools: true });
	}, [updatePreferences]);

	const setPosition = useCallback(
		(position: DevToolsPosition) => {
			updatePreferences({ position });
		},
		[updatePreferences]
	);

	// During SSR or before hydration, return null to prevent flash
	// After hydration, return actual preference
	const isVisible = isHydrated ? preferences.showDevTools : null;
	const position = isHydrated ? preferences.position : 'bottom-left';

	return {
		preferences,
		updatePreferences,
		hideDevTools,
		showDevTools,
		setPosition,
		position,
		isVisible,
		isHydrated
	};
};

export default useDevToolsPreferences;
